import { useEffect, useMemo, useState } from "react";
import { usePageState } from "@/contexts/NavigationContext";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { toast } from "sonner";
import { FileXlsIcon, PlayIcon, CaretRightIcon, MagnifyingGlassIcon } from "@phosphor-icons/react";
import SalesDynamicReport from "@/pages/reports/SalesDynamicReport";
import { getToken } from "@/lib/token";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

const fmtVal = (v, t, moneda) => {
  if (v === null || v === undefined || v === "") return "";
  if (t === "m") return `${moneda === "$" ? "$" : "L"} ${Number(v).toLocaleString("es-HN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (t === "n") return Number(v).toLocaleString("es-HN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (t === "i") return Number(v).toLocaleString("es-HN");
  if (t === "p") return `${Number(v).toFixed(2)}%`;
  return String(v);
};

export default function ReportsCenter() {
  return (
    <Layout title="Centro de Reportes" subtitle="Reportes operativos y financieros · Exportables a Excel (.xlsx)">
      <ReportsCatalog />
    </Layout>
  );
}

/** Catálogo de reportes exportables a Excel. `excluir`: ids que ya ofrece otra pestaña de REPORTES. */
export function ReportsCatalog({ excluir = [] }) {
  const [catalog, setCatalog] = useState([]);
  const [options, setOptions] = useState(null);
  const [active, setActive] = useState(null); // {id, titulo, filtros}
  const [search, setSearch] = usePageState("search", "");

  useEffect(() => {
    (async () => {
      const [c, o] = await Promise.all([api.get("/reports2/catalog"), api.get("/reports2/options")]);
      setCatalog(c.data.map((sec) => ({ ...sec, seccion: sec.seccion.toUpperCase(),
        reportes: sec.reportes.filter((r) => !excluir.includes(r.id)) })).filter((sec) => sec.reportes.length));
      setOptions(o.data);
    })();
  }, []);

  const filtered = useMemo(() => {
    if (!search) return catalog;
    const s = search.toLowerCase();
    return catalog.map((sec) => ({ ...sec, reportes: sec.reportes.filter((r) => r.titulo.toLowerCase().includes(s)) }))
      .filter((sec) => sec.reportes.length);
  }, [catalog, search]);

  return (
      <div className="flex gap-5 items-start">
        <div className="w-[320px] shrink-0 bg-card border border-border/60 rounded-lg overflow-hidden">
          <div className="p-3 border-b border-border/60 relative">
            <MagnifyingGlassIcon size={14} className="absolute left-6 top-1/2 -translate-y-1/2 text-text-muted" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="BUSCAR REPORTE…"
              data-testid="reports-search" className="w-full bg-app border border-border rounded-md pl-8 pr-3 py-2 text-sm" />
          </div>
          <div className="max-h-[calc(100vh-220px)] overflow-y-auto">
            {filtered.map((sec) => (
              <div key={sec.seccion}>
                <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-wider text-agro-green font-semibold">{sec.seccion}</div>
                {sec.reportes.map((r) => (
                  <button key={r.id} onClick={() => setActive(r)} data-testid={`report-item-${r.id}`}
                    className={`w-full text-left px-4 py-2 text-sm flex items-center justify-between hover:bg-white/[0.04] ${active?.id === r.id ? "bg-agro-green/10 text-agro-green" : "text-text-secondary"}`}>
                    <span className="truncate">{r.titulo}</span>
                    <CaretRightIcon size={12} className="shrink-0 opacity-50" />
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          {active ? (
            active.id === "ventas-dinamico"
              ? <SalesDynamicReport key={active.id} />
              : <ReportViewer key={active.id} report={active} options={options} />
          ) : <div className="bg-card border border-border/60 rounded-lg p-16 text-center text-text-muted" data-testid="reports-empty">
              SELECCIONA UN REPORTE DEL LISTADO PARA GENERARLO.<br />
              <span className="text-xs">Todos los reportes respetan los filtros aplicados y se exportan a Excel.</span>
            </div>}
        </div>
      </div>
  );
}

function ReportViewer({ report, options }) {
  const today = new Date().toISOString().slice(0, 10);
  const firstDay = today.slice(0, 8) + "01";
  const [f, setF] = useState({ desde: firstDay, hasta: today });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // «Mostrar observación» (memo / comentario de cada documento): se recuerda por reporte en este navegador
  const claveObs = `agroport_rep_obs_${report.id}`;
  const [verObs, setVerObs] = useState(() => { try { return localStorage.getItem(claveObs) !== "0"; } catch { return true; } });
  const cambiarObs = (v) => { setVerObs(v); try { localStorage.setItem(claveObs, v ? "1" : "0"); } catch { /* sin almacenamiento */ } };
  const tieneObs = Boolean(data?.columns?.some((c) => c.opt));
  const cols = (data?.columns || []).filter((c) => verObs || !c.opt);

  const qs = () => {
    const p = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => { if (v) p.set(k, v); });
    return p.toString();
  };

  const run = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/reports2/run/${report.id}?${qs()}`);
      setData(d);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error generando reporte"); }
    finally { setLoading(false); }
  };
  useEffect(() => { run(); /* eslint-disable-next-line */ }, []);

  const exportXlsx = async () => {
    try {
      const token = getToken();
      const res = await fetch(`${API}/reports2/run/${report.id}?${qs()}&format=xlsx${verObs ? "" : "&observacion=0"}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${report.id}-${today}.xlsx`;
      a.click();
      toast.success("Excel descargado");
    } catch (e) { toast.error(`Error exportando: ${e.message}`); }
  };

  const F = report.filtros || [];
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const sel = "bg-app border border-border rounded-md px-2.5 py-2 text-xs min-w-[130px]";

  return (
    <div className="bg-card border border-border/60 rounded-lg overflow-hidden" data-testid="report-viewer">
      <div className="p-4 border-b border-border/60 flex flex-wrap items-end gap-2">
        <div className="mr-auto">
          <h3 className="font-heading text-lg font-semibold">{data?.titulo || report.titulo}</h3>
          <div className="text-[11px] text-text-muted">{data ? `${data.rows.filter((r) => !r._g).length} filas` : ""}</div>
        </div>
        {(F.includes("rango")) && (<>
          <L l="Desde"><input type="date" value={f.desde || ""} onChange={set("desde")} className={sel} data-testid="filter-desde" /></L>
          <L l="Hasta"><input type="date" value={f.hasta || ""} onChange={set("hasta")} className={sel} data-testid="filter-hasta" /></L>
        </>)}
        {F.includes("fecha_corte") && <L l="A la fecha"><input type="date" value={f.hasta || ""} onChange={set("hasta")} className={sel} /></L>}
        {F.includes("condicion") && <L l="CONDICIÓN DE VENTA"><select value={f.condicion || ""} onChange={set("condicion")} className={sel} data-testid="filter-condicion">
          <option value="">AMBAS (CRÉDITO Y CONTADO)</option><option value="credito">CRÉDITO</option><option value="contado">CONTADO</option></select></L>}
        {F.includes("region") && <L l="Región"><select value={f.region || ""} onChange={set("region")} className={sel} data-testid="filter-region"><option value="">Todas</option>{options?.regiones?.map((r) => <option key={r}>{r}</option>)}</select></L>}
        {F.includes("proyecto") && <L l="Proyecto"><select value={f.proyecto || ""} onChange={set("proyecto")} className={sel} data-testid="filter-proyecto"><option value="">Todos</option>{options?.proyectos?.map((p) => <option key={p}>{p}</option>)}</select></L>}
        {F.includes("agrupar") && <L l="Agrupar por"><select value={f.agrupar || "cliente"} onChange={set("agrupar")} className={sel} data-testid="filter-agrupar">
          <option value="cliente">Cliente</option><option value="producto">Producto</option><option value="region">Región</option>
          <option value="rtc">RTC</option><option value="proyecto">Proyecto</option><option value="mes">Mes</option>
        </select></L>}
        {F.includes("comparar") && <L l="Comparar vs"><select value={f.comparar || ""} onChange={set("comparar")} className={sel} data-testid="filter-comparar">
          <option value="">Sin comparación</option>
          <option value="anio_anterior">Mismo período año anterior</option>
          <option value="periodo_anterior">Período inmediatamente anterior</option>
        </select></L>}
        {F.includes("bucket") && <L l="Dividir en"><select value={f.bucket || "semana"} onChange={set("bucket")} className={sel} data-testid="filter-bucket">
          <option value="semana">Semanas (7d)</option><option value="quincena">Quincenas (15d)</option><option value="mes">Meses (30d)</option>
        </select></L>}
        {F.includes("rtc") && <L l="RTC"><select value={f.rtc_id || ""} onChange={set("rtc_id")} className={sel}><option value="">Todos</option>{options?.rtcs?.map((r) => <option key={r.id} value={r.id}>{r.nombre}</option>)}</select></L>}
        {F.includes("cliente") && <L l="Cliente"><select value={f.cliente_id || ""} onChange={set("cliente_id")} className={sel} data-testid="filter-cliente"><option value="">Todos</option>{options?.clientes?.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}</select></L>}
        {F.includes("proveedor") && <L l="Proveedor"><select value={f.proveedor_id || ""} onChange={set("proveedor_id")} className={sel}><option value="">Todos</option>{options?.proveedores?.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}</select></L>}
        {F.includes("linea") && <L l="Línea"><select value={f.linea || ""} onChange={set("linea")} className={sel}><option value="">Todas</option>{options?.lineas?.map((l) => <option key={l}>{l}</option>)}</select></L>}
        {F.includes("ocultar_cero") && <L l="Existencia"><select value={f.ocultar_cero || ""} onChange={set("ocultar_cero")} className={sel} data-testid="filter-ocultar-cero"><option value="">Mostrar todos</option><option value="1">Ocultar existencia = 0</option></select></L>}
        {F.includes("cuenta") && <L l="Cuenta"><select value={f.cuenta_id || ""} onChange={set("cuenta_id")} className={sel} data-testid="filter-cuenta"><option value="">Todas (consolidado)</option>{options?.cuentas?.map((c) => <option key={c.id} value={c.id}>{c.nombre} ({c.moneda === "USD" ? "$" : "L"})</option>)}</select></L>}
        {F.includes("banco") && <L l="Banco"><select value={f.banco || ""} onChange={set("banco")} className={sel}><option value="">Todos</option>{options?.bancos?.map((b) => <option key={b}>{b}</option>)}</select></L>}
        {F.includes("cuenta_contable") && <L l="Cuenta contable"><select value={f.cuenta_contable || ""} onChange={set("cuenta_contable")} className={sel}><option value="">Todas</option>{options?.cuentas_contables?.map((c) => <option key={c.codigo} value={c.codigo}>{c.codigo} {c.nombre}</option>)}</select></L>}
        {F.includes("dias") && <L l="Días"><input type="number" value={f.dias || ""} onChange={set("dias")} placeholder="90" className={sel + " w-20"} /></L>}
        {F.includes("tasa") && <L l="Tasa L/$"><input type="number" step="0.01" value={f.tasa || ""} onChange={set("tasa")} placeholder="ej. 25.30" className={sel + " w-24"} /></L>}
        {tieneObs && (
          <label className="flex items-center gap-2 min-h-[36px] px-3 border border-border rounded-md text-xs cursor-pointer select-none" data-testid="toggle-observacion">
            <input type="checkbox" checked={verObs} onChange={(e) => cambiarObs(e.target.checked)} className="w-4 h-4" data-testid="toggle-observacion-check" />
            Mostrar observación
          </label>
        )}
        <button onClick={run} disabled={loading} data-testid="report-run"
          className="bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md px-4 py-2 text-xs flex items-center gap-1.5 disabled:opacity-50">
          <PlayIcon size={13} weight="bold" /> {loading ? "Generando…" : "Generar"}
        </button>
        <button onClick={exportXlsx} disabled={!data} data-testid="report-export-xlsx"
          className="border border-agro-green/50 text-agro-green hover:bg-agro-green/10 font-medium rounded-md px-4 py-2 text-xs flex items-center gap-1.5 disabled:opacity-40">
          <FileXlsIcon size={14} weight="bold" /> Exportar a Excel
        </button>
      </div>

      <div className="overflow-auto max-h-[calc(100vh-260px)]">
        {!data ? <div className="p-10 text-center text-text-muted text-sm">{loading ? "Generando…" : "Sin datos"}</div> : (
          <table className="w-full text-sm">
            <thead className="bg-sidebar sticky top-0 z-10">
              <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                {cols.map((c) => (
                  <th key={c.k} className={`p-2.5 whitespace-nowrap ${["m", "n", "i", "p"].includes(c.t) ? "text-right" : ""}`}>{c.l}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.length === 0 && <tr><td colSpan={cols.length} className="p-10 text-center text-text-muted">Sin resultados con los filtros aplicados</td></tr>}
              {data.rows.map((row, i) => row._g ? (
                <tr key={i} className="bg-agro-green/10"><td colSpan={cols.length} className="p-2 px-3 text-xs font-semibold text-agro-green uppercase tracking-wide">{row._g}</td></tr>
              ) : (
                <tr key={i} className={`border-t border-border/30 ${row._st ? "font-semibold bg-white/[0.03]" : "hover:bg-white/[0.02]"}`}>
                  {cols.map((c) => (
                    <td key={c.k} className={`p-2 px-2.5 text-xs ${c.opt ? "min-w-[240px] max-w-[420px] whitespace-normal text-text-secondary" : "whitespace-nowrap"} ${["m", "n", "i", "p"].includes(c.t) ? "text-right tabular-nums" : ""}`}>
                      {fmtVal(row[c.k], c.t, row.moneda)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            {data.total && (
              <tfoot className="sticky bottom-0 bg-sidebar">
                <tr className="border-t-2 border-agro-green font-bold" data-testid="report-total-row">
                  {cols.map((c) => (
                    <td key={c.k} className={`p-2.5 text-xs whitespace-nowrap ${["m", "n", "i", "p"].includes(c.t) ? "text-right tabular-nums text-agro-green" : ""}`}>
                      {fmtVal(data.total[c.k], c.t)}
                    </td>
                  ))}
                </tr>
              </tfoot>
            )}
          </table>
        )}
      </div>
    </div>
  );
}

const L = ({ l, children }) => (
  <label className="flex flex-col gap-1">
    <span className="text-[9px] uppercase tracking-wider text-text-muted">{l}</span>
    {children}
  </label>
);
